import React from 'react';
import { IString, ITableColumn, ITableExtension, Table } from '../../table';

import {
  DynamicRenderStyleMode,
  IConfigField,
  IListItem,
  IValue,
} from '../model';
import { DynamicComponentRender } from './adapter/dynamic-render';

interface IProps<T> {
  readonly configFields: ReadonlyArray<IConfigField<T>>;
  readonly list: ReadonlyArray<T>;
  readonly loading: boolean;
  readonly tableExtension?: ITableExtension<T>;
  readonly onChanged: <L>(
    key: string,
    value: L,
    row: IListItem,
    field: IConfigField<T>,
    index: number
  ) => void;

  readonly renderAllRow?: <L>(
    key: string,
    value: L,
    row: T,
    field: IConfigField<T>,
    index: number,
    filterValues?: IString<IValue>
  ) => React.ReactNode | null | string;
  readonly filterValues?: IString<IValue>;
}

export const WrapperComponent = <T,>(props: IProps<T>) => {
  const { configFields } = props;

  const onChanged = (row: IListItem, field: IConfigField<T>, index: number) => {
    return (key: string, value: any) => {
      props.onChanged(key, value, row, field, index);
    };
  };

  // 这里是内置的适配组件，不能包含所有功能
  // 额外提供插槽处理，用户可自定义组件处理
  const adapterRender = (
    text: string,
    row: IListItem,
    field: IConfigField<T>,
    index: number
  ) => {
    const selectedValue = {
      key: field.key,
      value: text,
    };
    return (
      <DynamicComponentRender
        onChanged={onChanged(row, field, index)}
        fieldConfig={field}
        selectedValue={selectedValue}
        mode={DynamicRenderStyleMode.tableBody}
      />
    );
  };

  // 处理每一列，根据字段配置渲染不同的UI
  const getRowColumn = (
    field: IConfigField<T>,
    configFields: ReadonlyArray<IConfigField<T>>
  ): ITableColumn<T> => {
    return {
      ...field.tableExtension,
      title: field.name,
      dataIndex: field.key,
      key: field.key,
      render: (text: string, row: T, columnIndex: number, rowIndex: number) => {
        // 渲染每一行
        if (field.tableExtension?.renderRow) {
          const element = field.tableExtension.renderRow(
            field.key,
            text,
            row,
            field,
            rowIndex,
            configFields,
            props.filterValues
          );
          if (element !== null) {
            return element;
          }
        }

        // 劫持全部Table Row渲染
        if (props.renderAllRow) {
          const element = props.renderAllRow(
            field.key,
            text,
            row,
            field,
            rowIndex,
            props.filterValues
          );
          if (element !== null) {
            return element;
          }
        }

        // AutoTable内置渲染器
        return adapterRender(text, row, field, rowIndex);
      },
    };
  };

  const getColumns = () => {
    return configFields
      .filter((field) => field.is_shown !== false)
      .map((field) => {
        return getRowColumn(field, configFields);
      });
  };
  return (
    <>
      <Table
        dataSource={props.list}
        columns={getColumns()}
        loading={props.loading}
        {...props.tableExtension}
      ></Table>
    </>
  );
};
